/**
 * SupportCampaignBanner — V9.19
 * Bannière CTA pour une campagne SupportCampaign V1 active (Athlète OU Club).
 * Ne s'affiche que si une campagne ACTIVE existe pour le bénéficiaire.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shirt, HandHeart, ArrowRight } from "lucide-react";

export function SupportCampaignBanner({ beneficiaryType, beneficiaryId }) {
  const [state, setState] = useState({ active: false, campaign: null });

  useEffect(() => {
    if (!beneficiaryType || !beneficiaryId) return;
    let cancelled = false;
    api
      .get(`/support-campaigns/beneficiary/${beneficiaryType}/${beneficiaryId}/active`)
      .then((r) => {
        if (!cancelled) setState(r.data || { active: false, campaign: null });
      })
      .catch(() => {
        if (!cancelled) setState({ active: false, campaign: null });
      });
    return () => {
      cancelled = true;
    };
  }, [beneficiaryType, beneficiaryId]);

  if (!state.active || !state.campaign) return null;
  const c = state.campaign;
  const total = Number(c.total_preorders_all_tranches || 0);
  const goal = 20;
  const pct = Math.min(100, Math.round((total / goal) * 100));
  const ctaLabel =
    beneficiaryType === "club" ? "Soutenir ce club" : "Soutenir cet athlète";
  const price = c.tshirt_price_xpf || 4500;

  return (
    <Card
      className="p-4 border-l-4 border-[hsl(var(--aito-gold))] bg-[hsl(var(--aito-gold))]/5"
      data-testid="support-campaign-banner"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-11 w-11 rounded-md bg-[hsl(var(--aito-gold))]/20 flex items-center justify-center text-[hsl(var(--aito-gold))] shrink-0">
          <Shirt className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm">
            {ctaLabel}
          </div>
          <div className="text-xs text-muted-foreground line-clamp-2">
            {c.title} · T-shirt AITO.PF · {price.toLocaleString("fr-FR")} XPF
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <div className="h-2 flex-1 min-w-[120px] rounded-full bg-secondary overflow-hidden">
              <div
                className="h-full bg-[hsl(var(--aito-lagoon))]"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-[11px] font-semibold whitespace-nowrap">
              {total} précommande{total > 1 ? "s" : ""}
            </span>
          </div>
        </div>
        <Link to={`/soutien/${c.slug}`}>
          <Button
            size="sm"
            className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"
            data-testid="support-campaign-cta"
          >
            <HandHeart className="h-4 w-4 mr-1.5" />
            {ctaLabel}
            <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </Link>
      </div>
    </Card>
  );
}

export default SupportCampaignBanner;
